import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, Trash2, Search, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useApp } from "@/context/AppContext";
import { listAlerts, deleteAlert } from "@/lib/api";
import { euro } from "@/lib/format";

const TX = {
  over: { it: "Gestione avvisi", en: "Alert management", hr: "Upravljanje upozorenjima", sl: "Upravljanje opozoril", de: "Alarm-Verwaltung", hu: "Értesítések kezelése", cs: "Správa upozornění" },
  title: { it: "I tuoi avvisi di prezzo", en: "Your price alerts", hr: "Tvoja upozorenja", sl: "Tvoja cenovna opozorila", de: "Deine Preisalarme", hu: "Árértesítéseid", cs: "Tvoje cenová upozornění" },
  sub: { it: "Inserisci la tua email per vedere e disattivare gli avvisi attivi.", en: "Enter your email to view and disable your active alerts.", hr: "Unesi email za pregled i isključivanje upozorenja.", sl: "Vnesi svoj e-poštni naslov za ogled in izklop aktivnih opozoril.", de: "Gib deine E-Mail ein, um deine aktiven Alarme zu sehen und zu deaktivieren.", hu: "Add meg az e-mailed az aktív értesítéseid megtekintéséhez és kikapcsolásához.", cs: "Zadej e-mail pro zobrazení a vypnutí aktivních upozornění." },
  find: { it: "Trova i miei avvisi", en: "Find my alerts", hr: "Pronađi upozorenja", sl: "Poišči moja opozorila", de: "Meine Alarme finden", hu: "Értesítéseim keresése", cs: "Najít moje upozornění" },
  empty: { it: "Nessun avviso attivo per questa email.", en: "No active alerts for this email.", hr: "Nema aktivnih upozorenja za ovaj email.", sl: "Ni aktivnih opozoril za ta e-poštni naslov.", de: "Keine aktiven Alarme für diese E-Mail.", hu: "Nincs aktív értesítés ehhez az e-mailhez.", cs: "Žádná aktivní upozornění pro tento e-mail." },
  target: { it: "Soglia", en: "Target", hr: "Prag", sl: "Meja", de: "Zielwert", hu: "Célár", cs: "Limit" },
  current: { it: "Attuale", en: "Current", hr: "Trenutno", sl: "Trenutno", de: "Aktuell", hu: "Jelenlegi", cs: "Aktuální" },
  hit: { it: "Sotto la soglia!", en: "Below target!", hr: "Ispod praga!", sl: "Pod mejo!", de: "Unter dem Zielwert!", hu: "A célár alatt!", cs: "Pod limitem!" },
  disable: { it: "Disattiva", en: "Disable", hr: "Isključi", sl: "Izklopi", de: "Deaktivieren", hu: "Kikapcsolás", cs: "Vypnout" },
  disabled: { it: "Avviso disattivato", en: "Alert disabled", hr: "Upozorenje isključeno", sl: "Opozorilo izklopljeno", de: "Alarm deaktiviert", hu: "Értesítés kikapcsolva", cs: "Upozornění vypnuto" },
};

export default function AlertsManage() {
  const { lang } = useApp();
  const [email, setEmail] = useState("");
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(false);
  const c = (k) => TX[k][lang] ?? TX[k].en;

  const search = async (e) => {
    e?.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      setAlerts(await listAlerts(email));
    } catch {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteAlert(id);
      setAlerts((a) => a.filter((x) => x.id !== id));
      toast.success(c("disabled"));
    } catch {
      toast.error("Error");
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-sand">
      <Navbar solid />
      <div className="mx-auto max-w-3xl px-5 pb-24 pt-28 lg:px-8">
        <p className="overline text-terracotta">{c("over")}</p>
        <h1 className="mt-1 font-serif text-4xl font-medium text-petrol lg:text-5xl">{c("title")}</h1>
        <p className="mt-3 text-petrol/70">{c("sub")}</p>

        <form onSubmit={search} className="mt-6 flex gap-3">
          <input
            type="email"
            data-testid="alerts-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            className="flex-1 rounded-none border border-sand-border bg-white px-5 py-3 text-sm outline-none focus:border-terracotta"
          />
          <button
            type="submit"
            data-testid="alerts-search-btn"
            className="flex items-center gap-2 rounded-none bg-terracotta px-6 py-3 font-semibold text-white transition-colors hover:bg-terracotta-hover"
          >
            <Search size={16} /> {c("find")}
          </button>
        </form>

        <div className="mt-8 flex flex-col gap-3">
          {loading && <div className="h-24 animate-pulse rounded-2xl bg-sand-card" />}
          {alerts && alerts.length === 0 && !loading && (
            <p className="rounded-2xl border border-dashed border-sand-border bg-sand-card py-12 text-center text-petrol/60">
              {c("empty")}
            </p>
          )}
          {alerts?.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid="alert-row"
              className="flex items-center gap-4 rounded-2xl border border-sand-border bg-sand-card p-3"
            >
              <img src={a.image} alt={a.name} className="h-20 w-24 rounded-xl object-cover" />
              <div className="flex-1">
                <Link to={`/stay/${a.slug}`} className="font-serif text-lg font-semibold text-petrol hover:text-terracotta">
                  {a.name}
                </Link>
                <p className="text-xs text-petrol/50">{a.zone_name}</p>
                <div className="mt-1 flex items-center gap-3 text-sm">
                  <span className="flex items-center gap-1 text-petrol/70">
                    <Bell size={13} className="text-terracotta" /> {c("target")} €{a.target_price}
                  </span>
                  <span className="text-petrol/70">{c("current")} {euro(a.current_price)}</span>
                  {a.triggered && (
                    <span className="flex items-center gap-1 rounded-none bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                      <TrendingDown size={12} /> {c("hit")}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => remove(a.id)}
                data-testid={`alert-disable-${a.id}`}
                className="flex items-center gap-1.5 rounded-none border border-sand-border px-4 py-2 text-sm font-semibold text-petrol transition-colors hover:border-terracotta hover:text-terracotta"
              >
                <Trash2 size={14} /> {c("disable")}
              </button>
            </motion.div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
